<template>
  <a-row :gutter="16">
    <!-- ============ TOP10 柱状图 ============ -->
    <a-col :span="14">
      <a-card :loading="loading" title="商户 TOP10（按订单总额）">
        <div v-if="!data?.topMerchants?.length" class="empty-tip">
          暂无数据
        </div>
        <div v-else ref="topChart" style="width: 100%; height: 400px;"></div>
      </a-card>
    </a-col>

    <!-- ============ 日期 × 商户 联动表 ============ -->
    <a-col :span="10">
      <a-card :loading="loading" title="日期 × 商户 联动">
        <a-input-search
            v-model="linkedFilter"
            placeholder="按商户编号/名称搜索"
            style="margin-bottom: 12px;"
            allow-clear
        />
        <a-table
            :columns="linkedColumns"
            :data-source="filteredDailyData"
            :pagination="false"
            :scroll="{ y: 400 }"
            row-key="(record) => `${record.date}-${record.merchantNo}`"
            size="small"
        >
          <template slot="amount" slot-scope="text">
            <Balance :amount="text || 0"/>
          </template>
        </a-table>
      </a-card>
    </a-col>
  </a-row>
</template>

<script>
import * as echarts from 'echarts';
import Balance from '@/view/components/Balance.vue';

export default {
  name: 'GlobalCockpitChart',
  components: {Balance},
  props: {
    data: {
      type: Object,
      default: () => ({}),
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      topChart: undefined,
      linkedFilter: '',  // 联动表商户搜索
      linkedColumns: [
        {title: '日期', dataIndex: 'date', width: 100, align: 'center', fixed: 'left'},
        {title: '商户', dataIndex: 'merchantName', width: 120, align: 'center'},
        {title: '金额(元)', dataIndex: 'amount', width: 120, align: 'right', scopedSlots: {customRender: 'amount'}},
        {title: '笔数', dataIndex: 'count', width: 70, align: 'center'},
      ],
    };
  },
  computed: {
    /**
     * 联动表按商户过滤
     */
    filteredDailyData() {
      const data = this.data?.dailyByMerchant || [];
      const kw = (this.linkedFilter || '').trim().toLowerCase();
      if (!kw) return data;
      return data.filter(d =>
        (d.merchantNo && d.merchantNo.toLowerCase().includes(kw)) ||
        (d.merchantName && d.merchantName.toLowerCase().includes(kw))
      );
    },
  },
  watch: {
    'data.topMerchants': {
      handler(newVal) {
        if (newVal) {
          this.$nextTick(() => {
            this.renderTopChart();
          });
        }
      },
      deep: true,
    },
  },
  mounted() {
    if (this.data?.topMerchants?.length) {
      this.$nextTick(() => {
        this.renderTopChart();
      });
    }
    window.addEventListener('resize', this.handleResize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
    if (this.topChart) {
      this.topChart.dispose();
      this.topChart = undefined;
    }
  },
  methods: {
/**
     * 渲染 TOP10 柱状图
     */
    renderTopChart() {
      const topMerchants = this.data?.topMerchants || [];
      if (!topMerchants.length || !this.$refs.topChart) return;

      // 反转使 TOP1 在最上方
      const reversed = [...topMerchants].reverse();
      // 展示完整商户名称（不拼接商户编号，避免过宽）
      const merchantNames = reversed.map((m) => m.merchantName || '');
      const amounts = reversed.map((m) => Number(m.totalAmount || 0));

      if (!this.topChart) {
        this.topChart = echarts.init(this.$refs.topChart);
      }
      this.topChart.setOption({
        tooltip: {
          trigger: 'axis',
          axisPointer: {type: 'shadow'},
          formatter: (params) => {
            const item = params[0];
            const merchant = reversed[item.dataIndex];
            return `
              <div style="font-weight:600">${merchant.merchantName}</div>
              <div>编号：${merchant.merchantNo}</div>
              <div>订单总额：${merchant.totalAmount} 元</div>
              <div>订单笔数：${merchant.totalCount}</div>
              <div>服务费：${merchant.serviceFee || 0} 元</div>
              <div>分润：${merchant.profitShare || 0} 元</div>
            `;
          },
        },
        grid: {left: 200, right: 50, top: 20, bottom: 30},
        xAxis: {type: 'value'},
        yAxis: {
          type: 'category',
          data: merchantNames,
          axisLabel: {fontSize: 12},
        },
        series: [{
          type: 'bar',
          data: amounts,
          itemStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 1, 0, [
              {offset: 0, color: '#91d5ff'},
              {offset: 1, color: '#1890ff'},
            ]),
            borderRadius: [0, 4, 4, 0],
          },
          label: {
            show: true,
            position: 'right',
            formatter: (params) => {
              const v = params.value;
              return v >= 10000 ? `${(v / 10000).toFixed(2)}万` : `${v}`;
            },
            fontSize: 11,
            color: '#666',
          },
        }],
      });
    },

    /**
     * 窗口大小变化
     */
    handleResize() {
      if (this.topChart) {
        this.topChart.resize();
      }
    },
  },
};
</script>

<style lang="less" scoped>
.empty-tip {
  height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #999;
  font-size: 14px;
}
</style>